import React, { useState, useEffect } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import axios from 'axios';
import DayColumn from './DayColumn';
import ChangeReasonModal from './ChangeReasonModal';
import JobDetailPanel from './JobDetailPanel';
import {
  generateDates,
  formatDateKey,
  addDaysToDate,
  groupJobsByDate,
  moveJobInState
} from './dateUtils';
import style from './CalendarView.module.sass';

// Seniors-only filter: job-string prefix "S0" (S01, S010, …). Matches
// the literal rule the user asked for — note SEN0-prefixed jobs do NOT
// qualify since they don't start with "S0".
const isSeniorJob = (job) => (job.job || '').toUpperCase().startsWith('S0');

const CalendarView = ({ startDate, seniorsOnly }) => {
  const [jobs, setJobs] = useState({});
  const [loading, setLoading] = useState(true);
  const [pendingMove, setPendingMove] = useState(null);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [pendingJobId, setPendingJobId] = useState(null);

  // Generate 14 days (Mon-Sun for 2 weeks)
  const dates = generateDates(startDate);

  // Check URL for job_id parameter to auto-open detail panel
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const jobId = urlParams.get('job_id');
    if (jobId) {
      setPendingJobId(jobId);
      // Clean up URL without reloading page
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [startDate]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const endDate = addDaysToDate(startDate, 13); // 2 weeks
      const response = await axios.get('/api/v1/calendar_jobs', {
        params: {
          start_date: formatDateKey(startDate),
          end_date: formatDateKey(endDate)
        }
      });

      // Group jobs by date
      const grouped = groupJobsByDate(response.data.data || []);
      setJobs(grouped);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    }
    setLoading(false);
  };

  // Auto-open detail panel when jobs are loaded and there's a pending job ID
  useEffect(() => {
    if (pendingJobId && Object.keys(jobs).length > 0) {
      // Find the job in all date groups
      for (const dateKey of Object.keys(jobs)) {
        const job = jobs[dateKey].find(j => String(j.id) === String(pendingJobId));
        if (job) {
          setSelectedJob(job);
          setShowDetailPanel(true);
          setPendingJobId(null);
          break;
        }
      }
    }
  }, [jobs, pendingJobId]);

  const handleDragEnd = (result) => {
    const { source, destination, draggableId } = result;

    // Dropped outside or same position
    if (!destination || source.droppableId === destination.droppableId) {
      return;
    }

    // Store pending move and show modal for reason
    setPendingMove({
      jobId: draggableId,
      oldDate: source.droppableId,
      newDate: destination.droppableId
    });
    setShowReasonModal(true);
  };

  const handleConfirmMove = async (reason) => {
    if (!pendingMove) return;

    try {
      const response = await axios.patch(`/api/v1/calendar_jobs/${pendingMove.jobId}/move`, {
        new_date: pendingMove.newDate,
        reason: reason
      });

      // Update local state with the job data from API response (includes correct has_changes)
      const updatedJob = response.data.djob;
      setJobs(prevJobs => {
        const newJobs = { ...prevJobs };

        // Remove job from old date
        const oldDateJobs = newJobs[pendingMove.oldDate] || [];
        const jobIndex = oldDateJobs.findIndex(j => j.id.toString() === pendingMove.jobId);
        if (jobIndex !== -1) {
          oldDateJobs.splice(jobIndex, 1);
        }

        // Add updated job to new date
        if (!newJobs[pendingMove.newDate]) newJobs[pendingMove.newDate] = [];
        newJobs[pendingMove.newDate].push(updatedJob);

        // Sort by school name
        newJobs[pendingMove.newDate].sort((a, b) =>
          (a.school_name || '').localeCompare(b.school_name || '')
        );

        return newJobs;
      });

      setShowReasonModal(false);
      setPendingMove(null);
    } catch (error) {
      alert('Failed to move job: ' + (error.response?.data?.error || error.message));
      // Refresh to restore original state
      fetchJobs();
      setShowReasonModal(false);
      setPendingMove(null);
    }
  };

  const handleCancelMove = () => {
    setShowReasonModal(false);
    setPendingMove(null);
  };

  const handleOpenDetail = (job) => {
    setSelectedJob(job);
    setShowDetailPanel(true);
  };

  const handleCloseDetail = () => {
    setShowDetailPanel(false);
    setSelectedJob(null);
  };

  const handleJobUpdated = () => {
    fetchJobs();
  };

  if (loading) {
    return <div className={style.loading}>Loading jobs...</div>;
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className={style.calendarContainer}>
        {showDetailPanel && (
          <div className={style.panelOverlay} onClick={handleCloseDetail} />
        )}

        <div className={style.calendarGrid}>
          {dates.map(date => {
            const dayJobs = jobs[formatDateKey(date)] || [];
            const visibleJobs = seniorsOnly ? dayJobs.filter(isSeniorJob) : dayJobs;
            return (
              <DayColumn
                key={formatDateKey(date)}
                date={date}
                jobs={visibleJobs}
                onOpenDetail={handleOpenDetail}
              />
            );
          })}
        </div>

        {showReasonModal && (
          <ChangeReasonModal
            onConfirm={handleConfirmMove}
            onCancel={handleCancelMove}
          />
        )}

        <JobDetailPanel
          job={selectedJob}
          open={showDetailPanel}
          onClose={handleCloseDetail}
          onJobUpdated={handleJobUpdated}
        />
      </div>
    </DragDropContext>
  );
};

export default CalendarView;
